import { lazy, FunctionComponent, Suspense, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Field from "../field";
import Label from "../label";
import {
  StyledIcon,
  StyledInput,
  StyledInputContainer,
} from "./datapicker.styles";
import Warning from "../warning";
import "react-datepicker/dist/react-datepicker.css";
import { faCalendar, faLock } from "components/icon-library";
import { useLocale } from "stores/hooks/locale";

// this a big one, so lazy load
const ReactDatepicker = lazy(() => import("react-datepicker"));

interface DatepickerProps {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  time?: boolean;
  min?: Date;
  max?: Date;
  clearable?: boolean;
}

const Datepicker: FunctionComponent<DatepickerProps> = ({
  name,
  label,
  required,
  disabled = false,
  placeholder,
  time = false,
  min,
  max,
  clearable = false,
}) => {
  const [hover, toggleHover] = useState(false);
  const locale = useLocale();

  // other states
  const { control, setValue, trigger } = useFormContext();

  const onChange = (dateValue: Date | null) => {
    // don't change, if disabled
    if (disabled) {
      return;
    }

    // set the value to state (not sure if needed?)
    setValue(name, dateValue);

    // trigger validation
    trigger(name);
  };

  return (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field: { ref, ...fieldProps } }) => (
        <Field>
          <Label name={name} required={required} disabled={disabled}>
            {label}
          </Label>
          <StyledInputContainer>
            <Suspense>
              <ReactDatepicker
                selected={fieldProps.value}
                onChange={onChange}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                calendarStartDay={1}
                todayButton={undefined}
                showIcon
                locale={locale}
                showWeekNumbers
                withPortal
                name={name}
                id={`id_${name}`}
                disabled={disabled}
                toggleCalendarOnIconClick
                isClearable={clearable}
                icon={<StyledIcon icon={disabled ? faLock : faCalendar} />}
                placeholderText={placeholder}
                minDate={min}
                maxDate={max}
                dateFormat={time ? "dd.MM.YYYY h:mm" : "dd.MM.YYYY"}
                customInput={
                  <StyledInput
                    key={name}
                    ref={ref}
                    onMouseEnter={() => toggleHover(true)}
                    onMouseLeave={() => toggleHover(false)}
                    $hover={hover}
                  />
                }
              />
            </Suspense>
          </StyledInputContainer>
          <Warning name={name} />
        </Field>
      )}
    />
  );
};

export default Datepicker;
